<template>
    <div>
        <page-header>
            Plan Information
        </page-header>
        <div class="max-w-screen-sm w-full flex flex-col gap-8">

            <vx-alert v-if="props.teamPlan?.attributes?.recharge"
                :closeable="false">
                To <a href="mailto:support@voxie.com?subject=I'd%20like%20to%20upgrade%20my%20Voxie%20plan&body=I'm%20interested%20in%20upgrading%20my%20plan%20in%20Voxie.%20Can%20you%20please%20send%20me%20more%20information%3F">upgrade {{ yourOrTheTeamsWord }} plan</a> or for other billing related questions, please contact <a href="mailto:support@voxie.com">support@voxie.com</a>.
            </vx-alert>
            <vx-alert v-if="!props.teamPlan"
                color="info"
                :closeable="false">{{ capitalize(yourOrTheTeamsWord) }} plan information could not be loaded. Please contact <a
                    href="https://support.voxie.com/hc/en-us/requests/new"
                    target="_blank"
                    rel="noreferrer">support</a>. </vx-alert>
            <vx-expandable-card v-if="kioskMode">
                <template v-slot:header>
                    Billing Configuration
                </template>
                <template v-slot:content>
                    <div class="flex flex-col gap-4">
                        <div v-if="team.parent_id">
                            <div v-if="team.is_billable">
                                This child team is billed for its usage.
                                Its usage is <strong>not</strong> included in the billing of its parent team.
                            </div>
                            <div v-else>
                                This child team is not billed for its usage.
                                Its usage is included in its parent team's usage.
                            </div>
                        </div>
                        <div>
                            <vx-button v-if="team.stripe_id"
                                       :href="`https://dashboard.stripe.com/customers/${encodeURIComponent(team.stripe_id)}`"
                                       target="_blank"
                                       rel="noopener noreferrer"
                                       class="w-full"
                                       color="muted"
                                       size="lg">
                                <span class="inline-block">Open in Stripe</span>
                                <font-awesome-icon widthAuto :icon="faArrowUpRightFromSquare"></font-awesome-icon>
                            </vx-button>
                            <vx-button v-else class="w-full" color="muted" size="lg" disabled>
                                This team is not linked to a Stripe customer
                            </vx-button>
                        </div>
                    </div>
                </template>
            </vx-expandable-card>
            <vx-expandable-card v-if="props.teamPlan">
                <template v-slot:header>
                    <span>{{ props.teamPlan.name }}</span>

                </template>
                <template v-if="upcomingInvoice" v-slot:headerRight>
                    <span>
                        {{ currencyCents(upcomingInvoice.total) }}
                        <span class="text-sm text-slate-400">
                            /{{ props.teamPlan.is_annual ? 'year' : 'month' }}
                        </span>
                    </span>
                </template>
                <template v-slot:content>
                    <div class="flex flex-col gap-4">
                        <div class="whitespace-pre-wrap">{{ planDescription }}</div>
                        <div v-if="loading"
                            class="py-3 text-center">
                            <vx-icon-loading class="w-9 h-9"
                                spin></vx-icon-loading>
                        </div>

                        <vx-alert v-else-if="error"
                            :closeable="false"
                            color="danger">Failed to load usage for this month.</vx-alert>

                        <template v-else>
                            <div v-if="props.teamPlan.attributes?.recharge">
                                <div v-if="props.teamMessageBalance != undefined && !props.teamPlan.attributes?.no_rollover">
                                    Current balance: {{ formatNumber(props.teamMessageBalance) }} segments
                                </div>
                                <div v-else-if="props.teamPlan.attributes?.no_rollover">
                                    Current balance: {{ formatNumber(currentBalanceForNoRollover) }} segments
                                </div>
                                <div>
                                    Usage this month: {{ formatNumber(totalCreditsUsed) }} segments
                                </div>
                                <div v-if="props.teamPlan.attributes?.no_rollover">
                                    Number of recharges: {{ formatNumber(numberOfRecharges) }}
                                </div>
                            </div>
                            <div v-if="!props.teamPlan.attributes?.recharge">
                                SMS usage this month: {{ formatNumber(usageByType.sms) }} segments <br />
                                MMS usage this month: {{ formatNumber(usageByType.mms) }} messages
                            </div>
                            <vx-alert color="info" :closeable="false">
                                These estimates can take a few hours to update and are not necessarily reflective of final invoice
                                amounts.
                            </vx-alert>
                        </template>
                    </div>
                </template>
            </vx-expandable-card>
            <vx-expandable-card v-if="upcomingInvoice" class="w-full">
                <template v-slot:header>
                    Estimated Invoice
                </template>
                <template v-slot:headerSubtitle>
                    This is an estimate based on usage so far this month.
                    These numbers can take a few hours to update and are not necessarily reflective of final invoice amounts.
                </template>
                <template v-slot:content>
                    <div class="flex flex-col gap-4 mt-6">
                        <invoice-preview :invoice="upcomingInvoice" />
                    </div>
                </template>
            </vx-expandable-card>
            <vx-expandable-card data-test="billing-email">
                <template v-slot:header>
                    Billing Email
                </template>
                <template v-slot:content>
                    <div v-if="props.kioskMode" class="text-slate-500">
                        {{ team.billing_email }}
                    </div>
                    <div v-else class="flex flex-col gap-2">
                        <vx-alert v-if="success"
                            type="success"
                            :closeable="false">Billing email has been successfully updated</vx-alert>
                        <vx-alert v-if="emailError"
                            type="danger"
                            :closeable="false">Billing email has failed to updated</vx-alert>
                        <vx-input :disabled="!props.isAdminOrOwner || team.status === 'closed'"
                            size="lg"
                            placeholder="Email for billing"
                            v-model="billingEmail"
                            :error="displayFirstError(validate.email)"></vx-input>
                        <div v-if="props.isAdminOrOwner">
                            <vx-button type="button"
                                size="lg"
                                :loading="saving"
                                :disabled="team.status === 'closed'"
                                @click.prevent="saveBillingEmail">Save <font-awesome-icon widthAuto
                                    :icon="faCheck"></font-awesome-icon></vx-button>
                        </div>
                    </div>

                </template>
            </vx-expandable-card>
        </div>
    </div>
</template>
<script setup>
import {faCheck, faArrowUpRightFromSquare} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxExpandableCard, VxIconLoading, VxInput } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { email, required } from '@vuelidate/validators';
import { computed, onMounted, ref } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import { analyticsClient , teamsClient } from '~/services';
import { getCurrentMonthDateRange } from '~/composables/useBillingDates';
import { capitalize, currencyCents } from '~/utils/helpers';
import { displayFirstError, validateRules } from '~/utils/validation';
import InvoicePreview from '~/components/settings/teams/plan-information/InvoicePreview.vue';

const team = defineModel({
    required: true,
    type: Object,
});

const props = defineProps({
    teamPlan: {
        type: Object
    },
    teamMessageBalance: {
        type: Number,
    },
    teamSumBalanceAmount: {
        type: Number,
    },
    kioskMode: {
        type: Boolean,
        default: false,
    },
    isAdminOrOwner: {
        type: Boolean,
        default: false,
    }
});

const error = ref(false);
const loading = ref(true);

const usageByTypeRaw = ref([]);

const upcomingInvoice = ref(null);

const usageByType = computed(() => {
    let mms = 0;
    let sms = 0;

    usageByTypeRaw.value.forEach((record) => {
        switch (record['usage_type']) {
            case 'mms_message':
                mms += record['values'][0];
                break;
            case 'sms_segment':
                sms += record['values'][0];
                break;
        }
    });

    return { mms, sms };
});

const yourOrTheTeamsWord = computed(() => !props.kioskMode ? 'your' : 'the team\'s');
const youAreOrTheTeamIsWord = computed(() => !props.kioskMode ? 'you are' : 'the team is');

const planDescription = computed(() => {
    const hasRecharge = props.teamPlan.attributes?.recharge;
    const hasRollover = !props.teamPlan.attributes?.no_rollover;
    const isUsageBased = !hasRecharge;

    if (hasRecharge && hasRollover) {
        return `This plan includes ${formatNumber(props.teamPlan.attributes?.messages)} segment credits with segment balance rollover. Unused segment credits will transfer to the next period and the rollover balance resets at the beginning of the calendar year.`;
    }

    if (hasRecharge && !hasRollover) {
        return `This plan includes ${formatNumber(props.teamPlan.attributes?.messages)} segment credits.
If the balance is completely used during a month, ${yourOrTheTeamsWord.value} card on file will immediately be charged $${formatNumber(Math.ceil(props.teamPlan.attributes?.recharge_cost / 100))} and ${formatNumber(props.teamPlan.attributes?.recharge_messages)} segment credits will be added to ${yourOrTheTeamsWord.value} balance.`;
    }

    if (isUsageBased) {
        return `${capitalize(youAreOrTheTeamIsWord.value)} on a custom usage-based plan. Please see ${yourOrTheTeamsWord.value} individual contract for billing details and rates.`;
    }

    return '';
});


const planMessages = computed(() => {
    return props.teamPlan?.attributes?.messages ?? 0;
})

const totalCreditsUsed = computed(() => {
    return (usageByType.value.mms * 2) + usageByType.value.sms;
});

const numberOfRecharges = computed(() => {
    if (!props.teamPlan?.attributes?.recharge) {
        return 0;
    }

    const numberOfRecharges = Math.ceil((totalCreditsUsed.value - planMessages.value) / props.teamSumBalanceAmount);
    return numberOfRecharges < 0 ? 0 : numberOfRecharges;
})

const currentBalanceForNoRollover = computed(() => {
    return planMessages.value + props.teamSumBalanceAmount
})

const formatNumber = (number) => {
    return Intl.NumberFormat('en-US').format(number);
}

const fetchUsageTotalByType = async () => {
    loading.value = true;
    const currentMonthDates = getCurrentMonthDateRange();

    if (!currentMonthDates) {
        usageByTypeRaw.value = [];
        loading.value = false;
        return;
    }

    try {
        const usageByTypeResponse = await analyticsClient.getUsageTotalByType(
            { teamId: team.value.id },
            {
                range: [currentMonthDates.start_date, currentMonthDates.end_date],
                filters: {
                    usage_types: ['mms_message', 'sms_segment'],
                },
            }
        );
        usageByTypeRaw.value = usageByTypeResponse.data?.data;
    } catch (e) {
        console.error(e);
        usageByTypeRaw.value = [];
        error.value = true;
    } finally {
        loading.value = false;
    }
}

const fetchUpcomingInvoice = async () => {
    try {
        const response = await teamsClient.fetchUpcomingInvoice(team.value.id);
        upcomingInvoice.value = response?.data?.invoice ? response.data.invoice : null;
    } catch (e) {
        console.error(e);
    }
}

onMounted(() => {
    fetchUsageTotalByType();
    fetchUpcomingInvoice();
})

const billingEmail = ref(team.value.billing_email);
const saving = ref(false);
const success = ref(false);
const emailError = ref(false);

const validate = useVuelidate({
    email: { required, email }
}, {
    email: billingEmail
})

const saveBillingEmail = async () => {
    if (saving.value) {
        return;
    }
    const isValid = await validateRules(validate);
    if (!isValid) {
        return;
    }
    success.value = false;
    emailError.value = false;

    try {
        await teamsClient.updateBillingEmail(team.value.id, { email: billingEmail.value });
        success.value = true;
    } catch (e) {
        console.error(e);
        emailError.value = true;
    } finally {
        saving.value = false;
    }
}
</script>
